// 보호자가 배송 완료 사진을 보는 공개 페이지 (비로그인 접근 — 문자 단축 링크 /p/:code 대상)
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/lib/firebase";
import { plainError } from "@/lib/plainError";

const PROOF_LINK_DAYS = 90; // 개인정보처리방침·firestore.rules·cleanupProofLinks와 같은 값

export default function ProofView() {
  const { code } = useParams();
  const [url, setUrl] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "error" | "expired">("loading");
  const [org, setOrg] = useState<{ name: string; phone: string }>({ name: "", phone: "" });
  // 이 링크로 알림이 꺼지는 학생 수 — 서버(optOutGuardianNotice)가 링크의 배송 기록(형제가 같은 사진이면 모두)을 끈다. 옛 링크는 1명
  const [kids, setKids] = useState(1);
  // 「알림 그만 받기」(대표 결정 Q14) — 이 링크의 학생만 서버가 끈다(링크 코드가 열쇠)
  const [optOut, setOptOut] = useState<"idle" | "confirm" | "busy" | "done" | "error">("idle");
  const [optOutMsg, setOptOutMsg] = useState("");
  const stopNotices = async () => {
    setOptOut("busy");
    try {
      await httpsCallable(functions, "optOutGuardianNotice")({ code });
      setOptOut("done");
    } catch (e) {
      setOptOutMsg(plainError(e));
      setOptOut("error");
    }
  };

  useEffect(() => {
    (async () => {
      if (!code) {
        setState("error");
        return;
      }
      try {
        const snap = await getDoc(doc(db, "proofLinks", code));
        const data = snap.exists() ? (snap.data() as any) : null;
        const u = data?.proofUrl || null;
        if (data) setOrg({ name: data.orgName || "", phone: data.orgPhone || "" });
        if (Array.isArray(data?.deliveryIds) && data.deliveryIds.length > 1) setKids(data.deliveryIds.length);
        // 만든 지 90일이 지난 링크 — 저장 규칙이 먼저 막지만(permission-denied), 규칙 배포 전 화면도 같은 기준으로 판단한다
        const createdMs = data?.createdAt?.toMillis?.() ?? 0;
        if (data && Date.now() - createdMs > PROOF_LINK_DAYS * 24 * 3600 * 1000) {
          setState("expired");
          return;
        }
        if (u) {
          setUrl(u);
          setState("ok");
        } else {
          setState("error");
        }
      } catch (e: any) {
        // 저장 규칙이 거부한 것 = 90일이 지난 링크(없는 링크는 규칙이 허용해 위 error로 간다)
        setState(e?.code === "permission-denied" ? "expired" : "error");
      }
    })();
  }, [code]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 gap-4">
      <div className="text-lg font-bold text-milk-700">🥛 무상우유 배송 완료</div>
      {state === "loading" && <div className="text-slate-500">사진 불러오는 중…</div>}
      {state === "error" && (
        <div className="text-slate-600 text-center text-sm space-y-2">
          <p>사진을 찾을 수 없습니다.<br />문자에 온 링크를 다시 눌러 주세요. 그래도 안 되면 문자를 보낸 우유 배송 업체에 연락해 주세요.</p>
          <button className="btn-secondary min-h-[44px]" onClick={() => location.reload()}>다시 시도</button>
        </div>
      )}
      {state === "expired" && (
        <div className="text-slate-600 text-center text-sm">
          <p>사진 보관 기간(배송 후 90일)이 지나 더 볼 수 없습니다.</p>
        </div>
      )}
      {state === "ok" && url && (
        <img src={url} alt="배송 완료 사진" className="max-w-full max-h-[80vh] rounded-2xl shadow-elevated" onError={() => setState("expired")} />
      )}
      {state !== "loading" && (org.name || org.phone) && (
        <div className="text-sm text-slate-500 text-center">
          {org.name && <span>{org.name}</span>}
          {org.phone && <a className="ml-2 underline text-milk-700 inline-block min-h-[44px] leading-[44px]" href={`tel:${org.phone.replace(/\D/g, "")}`}>문의 {org.phone}</a>}
        </div>
      )}
      {state === "ok" && (
        <div className="text-sm text-slate-500 text-center space-y-2">
          {optOut === "idle" && (
            <button className="underline text-slate-500 min-h-[44px]" onClick={() => setOptOut("confirm")}>배송 알림 그만 받기</button>
          )}
          {optOut === "confirm" && (
            <div className="card p-3 space-y-2">
              <p>{kids > 1 ? `이 집 아이들(${kids}명)의` : "이 학생의"} 우유 배송완료 문자를 더 보내지 않습니다. 우유 배송은 그대로 계속됩니다.</p>
              <div className="flex gap-2 justify-center">
                <button className="btn-primary min-h-[44px]" onClick={stopNotices}>알림 그만 받기</button>
                <button className="btn-secondary min-h-[44px]" onClick={() => setOptOut("idle")}>취소</button>
              </div>
            </div>
          )}
          {optOut === "busy" && <p>처리 중…</p>}
          {optOut === "done" && <p className="text-emerald-700">배송 알림을 껐습니다. 다시 받으시려면 우유 배송 업체에 말씀해 주세요.</p>}
          {optOut === "error" && (
            <div className="space-y-1">
              <p className="text-rose-700">알림을 끄지 못했습니다 — {optOutMsg}</p>
              <button className="btn-secondary min-h-[44px]" onClick={stopNotices}>다시 시도</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
